import { Position } from "geojson";
import { useEffect, useState } from "react";

import { WaterFlowPath, XYLocation } from "@nens/deck-gl";
import { getDistanceInM } from "@nens/shared-utils";

import { Asset } from "../../../types/assets";
import simplify from "../helpers/simplify";

// *** EXPLANATION ***
// This code converts all the loose assets into connected flows.
// It does this by first creating a flow. Then is looks through all the assets for one that connects at the end or the beginning.
// If it finds one it adds it to the flow and starts looking for the next asset.
// If the flow can't find any more assets it creates a new flow and starts over again untill all assets belong to a flow

const getAssetCrossSectionMaxWidth = (asset: Asset) => {
  const crossSectionProperties = asset.properties as any;
  let maxWidth = 100;
  let done = false;
  let checkIndex = 1;

  // Check max width with for type 5, 6, 7
  while (!done) {
    if ("cross_section_type_" + checkIndex in crossSectionProperties) {
      const type = crossSectionProperties["cross_section_type_" + checkIndex];
      if ([5, 6, 7].includes(type)) {
        const [xArray] = crossSectionProperties["cross_section_table_" + checkIndex];
        maxWidth = Math.max(...xArray);
      }
    } else done = true;
    checkIndex++;
  }

  // check max width for type 0, 1, 2, 3, 8
  const shape = crossSectionProperties["cross_section_shape"];
  if ([0, 1, 2, 3, 8].includes(shape)) maxWidth = crossSectionProperties["cross_section_width"];

  return maxWidth;
};

const useOneDFlows = (assets?: { [value: string]: Asset[] }) => {
  const [flows, setFlows] = useState<WaterFlowPath[][]>([]);

  useEffect(() => {
    if (!assets) return;
    // We have to make a copy of the assets so it doesnt break the other visualisations
    const copyAllAssets: Asset[] = JSON.parse(
      JSON.stringify([
        ...assets.channels,
        ...assets.pipes,
        ...assets.culverts,
        ...assets.orifices,
        ...assets.breaches,
        ...assets.weirs,
      ])
    );

    const getId = (asset: Asset) =>
      asset.properties.model_type === "breaches"
        ? asset.properties.levl // id of the related flowline for breach
        : asset.properties.id;

    const findIntersection = (connectionPoint: Position, ownId: number) => {
      return !!copyAllAssets.find((asset) => {
        if (asset.properties.id === ownId) return false;
        const assetCoordinates = asset.geometry.coordinates as Position[];
        const assetFirstCoordinates = assetCoordinates[0] as Position;
        const assetLastCoordinates = assetCoordinates[assetCoordinates.length - 1] as Position;

        if (getDistanceInM(connectionPoint, assetFirstCoordinates) === 0) return true;
        if (getDistanceInM(connectionPoint, assetLastCoordinates) === 0) return true;
        return false;
      });
    };

    const createPath = (
      asset: Asset,
      coordinates: Position[],
      lastPath?: WaterFlowPath,
      hasIntersection?: boolean
    ) => {
      const id = getId(asset);
      const maxWidth = getAssetCrossSectionMaxWidth(asset);

      return {
        coordinates,
        id,
        // This averageSpeedKey is used in the applied flows to make sure the animation doesnt jump on speed updates
        // It gets the last paths key unless the asset intersects with 2 other assets or if the connecting asset is of a different type
        averageSpeedKey:
          !hasIntersection && lastPath && asset.properties.model_type === lastPath.modelType
            ? lastPath.averageSpeedKey
            : id,
        modelType: asset.properties.model_type,
        pipeType:
          asset.properties.model_type === "pipes" ? asset.properties.sewerage_type : undefined,
        passedTime: 0,
        speed: 0,
        width: 0,
        maxWidth,
      } as WaterFlowPath;
    };

    const newFlows: WaterFlowPath[][] = [];

    const addConnectedAsset = () => {
      const flow = newFlows[newFlows.length - 1];
      const firstPath = flow[0];
      const firstPathCoordinates = firstPath.coordinates[0];
      const lastPath = flow[flow.length - 1];
      const lastPathCoordinates = lastPath.coordinates[lastPath.coordinates.length - 1];

      const connectedAsset = copyAllAssets.find((asset, index) => {
        const assetCoordinates = asset.geometry.coordinates as Position[];
        const assetFirstCoordinates = assetCoordinates[0] as Position;
        const assetLastCoordinates = assetCoordinates[assetCoordinates.length - 1] as Position;
        let foundConnection: "end" | "beginning" | null = null;

        if (assetCoordinates.length < 2) return false;
        if (getDistanceInM(lastPathCoordinates, assetFirstCoordinates) === 0)
          foundConnection = "end";
        if (getDistanceInM(firstPathCoordinates, assetLastCoordinates) === 0)
          foundConnection = "beginning";

        if (!foundConnection) return false;

        const useCoordinates = [...assetCoordinates];

        const findIntersectionCoordinates =
          foundConnection === "end"
            ? flow[flow.length - 1].coordinates.pop()
            : useCoordinates.pop();

        const hasIntersection =
          findIntersectionCoordinates &&
          findIntersection(findIntersectionCoordinates, asset.properties.id);

        const newPath = createPath(
          asset,
          useCoordinates,
          foundConnection === "end" ? lastPath : firstPath,
          hasIntersection
        );

        copyAllAssets.splice(index, 1);

        if (foundConnection === "end") flow.push(newPath);
        if (foundConnection === "beginning") flow.unshift(newPath);

        return true;
      });

      connectedAsset ? addConnectedAsset() : setTimeout(createNewFlow);
    };

    // Simplify the coordinates
    const simplifyAndApplyFlows = (flows: WaterFlowPath[][]) => {
      const applyFlows = flows.map((flow) =>
        flow.map((path) => ({
          ...path,
          coordinates: simplify(path.coordinates as XYLocation[], 0.00001, true),
        }))
      );
      setFlows(applyFlows);
    };

    const createNewFlow = () => {
      const newFlowFirstAsset = copyAllAssets.shift();

      if (!newFlowFirstAsset) return simplifyAndApplyFlows(newFlows); //This applies the calculated flows if there are no more new assets
      const flowCoordinates = newFlowFirstAsset.geometry.coordinates as Position[];
      if (flowCoordinates.length < 2) {
        createNewFlow();
        return;
      }

      newFlows.push([createPath(newFlowFirstAsset, flowCoordinates)]);
      addConnectedAsset();
    };

    createNewFlow();
  }, [assets]);

  return flows;
};

export default useOneDFlows;
